import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { copy } from "../copy";
import { fetchSiteKey, register, safeNext } from "../auth/api";
import AuthField from "../auth/AuthField";
import AuthShell, { AuthAlert } from "../auth/AuthShell";
import GoogleButton from "../auth/GoogleButton";
import { preloadRecaptcha, recaptchaToken } from "../auth/recaptcha";
import Icon from "../components/Icon";
import { fullPageLoad } from "../navigate";
import { useSession } from "../session";

const t = copy.auth.register;

// Same rules as the server; the server checks again.
const USERNAME = /^[A-Za-z0-9_.-]{3,30}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function passwordScore(value: string): number {
  let score = 0;
  if (value.length >= 8) score += 1;
  if (/[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  return score;
}

const STRENGTH_COLORS = ["bg-ground-edge", "bg-alert", "bg-gold", "bg-gold", "bg-money"];

export default function Register() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const session = useSession();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("producer");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);
  const [website, setWebsite] = useState("");
  const [siteKey, setSiteKey] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const score = passwordScore(password);

  useEffect(() => {
    if (session.kind !== "visitor") fullPageLoad(next);
  }, [session.kind, next]);

  useEffect(() => {
    let live = true;
    void fetchSiteKey().then((key) => {
      if (!live) return;
      setSiteKey(key);
      preloadRecaptcha(key);
    });
    return () => {
      live = false;
    };
  }, []);

  const check = (): string => {
    if (!USERNAME.test(username.trim())) return t.badUsername;
    if (!EMAIL.test(email.trim())) return t.badEmail;
    if (password.length < 8) return t.shortPassword;
    if (password !== confirm) return t.mismatch;
    if (!agree) return t.mustAgree;
    return "";
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const problem = check();
    setError(problem);
    if (problem) return;

    setSubmitting(true);
    let token = "";
    try {
      token = await recaptchaToken(siteKey);
    } catch {
      setError(t.captchaFailed);
      setSubmitting(false);
      return;
    }
    const result = await register({
      username: username.trim(),
      email: email.trim(),
      password,
      role,
      recaptcha_token: token,
      website,
    });
    if (result.ok) {
      fullPageLoad(next);
      return;
    }
    setError(result.error);
    setSubmitting(false);
  };

  return (
    <AuthShell
      title={t.title}
      eyebrow={t.eyebrow}
      heading={t.heading}
      sub={t.sub}
      footer={
        <>
          {t.footer}{" "}
          <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="bf-focusable font-bold text-gold hover:text-gold-light">
            {t.footerLink}
          </Link>
        </>
      }
    >
      <GoogleButton onDone={() => fullPageLoad(next)} onError={setError} />
      <form onSubmit={submit} noValidate className="flex flex-col gap-5">
        {error && <AuthAlert message={error} />}
        <AuthField
          id="username"
          label={t.username}
          icon="user"
          autoComplete="username"
          value={username}
          placeholder={t.usernamePlaceholder}
          onChange={setUsername}
        />
        <AuthField
          id="email"
          label={copy.auth.email}
          icon="mail"
          type="email"
          autoComplete="email"
          value={email}
          placeholder={copy.auth.emailPlaceholder}
          onChange={setEmail}
        />

        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim">{t.role}</p>
          <div className="flex gap-1.5 rounded-full border border-ground-edge bg-ground-raised p-1.5">
            {t.roles.map((option) => {
              const active = role === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRole(option.value)}
                  aria-pressed={active}
                  className={`bf-focusable flex-1 whitespace-nowrap rounded-full px-3 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    active ? "bg-money text-ground" : "text-chrome-dim hover:text-chrome"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <AuthField
            id="password"
            label={copy.auth.password}
            icon="lock"
            type="password"
            revealable
            autoComplete="new-password"
            value={password}
            placeholder={t.passwordPlaceholder}
            onChange={setPassword}
          />
          {password.length > 0 && (
            <div className="mt-3">
              <div className="flex gap-1.5" aria-hidden>
                {[0, 1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={`h-1 flex-1 rounded-full ${step < score ? STRENGTH_COLORS[score] : "bg-ground-edge"}`}
                  />
                ))}
              </div>
              <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-chrome-dim">
                {t.strength}: {t.strengthLabels[score]}
              </p>
            </div>
          )}
        </div>

        <AuthField
          id="confirm"
          label={t.confirm}
          icon="lockPassword"
          type="password"
          revealable
          autoComplete="new-password"
          value={confirm}
          placeholder={t.confirmPlaceholder}
          onChange={setConfirm}
        />

        {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
        <div aria-hidden className="absolute -left-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <button
          type="button"
          onClick={() => setAgree((v) => !v)}
          aria-pressed={agree}
          className="bf-focusable flex items-start gap-3 text-left text-xs leading-relaxed text-chrome-dim transition-colors hover:text-chrome"
        >
          <span
            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-xs transition-colors ${
              agree ? "border-money bg-money text-ground" : "border-ground-edge"
            }`}
          >
            {agree && <Icon name="check" />}
          </span>
          {t.agree}
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="bf-focusable mt-1 flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-money text-sm font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? t.submitting : t.submit}
          {!submitting && <Icon name="arrowRight" className="text-base" />}
        </button>
        {siteKey && <p className="text-[10px] leading-relaxed text-chrome-dim">{t.recaptchaNote}</p>}
      </form>
    </AuthShell>
  );
}
